import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import { ToastContainer } from './components/common/ToastContainer';
import { Header } from './components/layout/Header';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { Deals } from './pages/Deals';
import { DealDetail } from './pages/DealDetail';
import { Templates } from './pages/Templates';
import { Analytics } from './pages/Analytics';
import { Settings } from './pages/Settings';
import { Pricing } from './pages/Pricing';
import { PublicRoom } from './pages/PublicRoom';
import { useTheme } from './hooks/useTheme';
import type { ReactNode } from 'react';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--color-border)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { theme, toggle } = useTheme();
  useAuth();

  return (
    <>
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Header theme={theme} onToggleTheme={toggle} />
      <div style={{ paddingTop: '3.5rem' }}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/verify" element={<Login />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/room/:token" element={<PublicRoom />} />

          {/* Protected */}
          <Route path="/dashboard" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/deals" element={<ProtectedRoute><Deals /></ProtectedRoute>} />
          <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <footer role="contentinfo" style={{
        textAlign: 'center', padding: 'var(--space-8) var(--space-4)',
        borderTop: '1px solid var(--color-border-subtle)',
        color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)',
      }}>
        &copy; 2026 딜룸. All rights reserved.
      </footer>
      <ToastContainer />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
